import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { IconDef } from '../icons/icon';
import { IconComponent } from '../icons/icon-reusable';

@Component({
  selector: 'app-button-reusable',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [ngClass]="computedClasses"
      (click)="onClick($event)"
    >
      <span *ngIf="loading" class="mr-2 flex items-center">
        <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" fill="none"></path>
        </svg>
      </span>

      <span *ngIf="!loading && hasIcon && icon && iconPosition === 'left'" [ngClass]="iconContainerClasses">
        <app-icon-reusable
          [icon]="icon"
          [size]="iconSize"
          [useCurrentColor]="iconUseCurrentColor"
          [classes]="iconClasses"
        ></app-icon-reusable>
      </span>

      <span [ngClass]="projectedContentClasses">
        <ng-content></ng-content>
      </span>

      <span *ngIf="!loading && hasIcon && icon && iconPosition === 'right'" [ngClass]="iconContainerClasses">
        <app-icon-reusable
          [icon]="icon"
          [size]="iconSize"
          [useCurrentColor]="iconUseCurrentColor"
          [classes]="iconClasses"
        ></app-icon-reusable>
      </span>
    </button>
  `
})
export class ButtonComponent {

  @Input() type: 'button' | 'submit' | 'reset' = 'button';

  @Input() variant: 'primary' | 'success' | 'danger' | 'neutral' | 'amber' = 'primary';

  @Input() appearance: 'filled' | 'outline' = 'filled';

  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  @Input() fullWidth = false;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() extraClasses = '';
  @Input() hasIcon = false;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() icon: IconDef | null = null;
  @Input() iconUseCurrentColor = false;
  @Input() iconClasses = '';
  @Input() iconTextGap = 'gap-1';

  @Output() clicked = new EventEmitter<Event>();

  private styles = {
    primary: {
      filled: 'bg-indigo600 text-white hover:bg-indigo500 hover:text-white active:bg-indigo800 active:text-white',
      outline: 'border border-indigo600 text-indigo600 hover:border-indigo500 hover:text-indigo500 active:border-indigo800 active:text-indigo800',
      disabled: 'bg-neutral50 text-neutral400 border border-neutral400'
    },

    success: {
      filled: 'bg-green600 text-white hover:bg-green500 hover:text-white active:bg-green800 active:text-white',
      outline: 'border border-green600 text-green600 hover:border-green500 hover:text-green500 active:border-green800 active:text-green800',
      disabled: 'bg-neutral50 text-neutral400 border border-neutral400'
    },

    danger: {
      filled: 'bg-red600 text-white hover:bg-red500 hover:text-white active:bg-red800 active:text-white',
      outline: 'border border-red600 text-red600 hover:border-red500 hover:text-red500 active:border-red800 active:text-red800',
      disabled: 'bg-neutral50 text-neutral400 border border-neutral400'
    },

    neutral: {
      filled: 'bg-white text-slate600 hover:bg-slate50 hover:text-slate600 active:bg-slate50  border border-slate300 active:text-slate400',
      outline: 'border border-slate300 text-slate600 hover:border-neutral200 hover:text-slate600 active:border-slate600 active:text-slate400 ',
      disabled: 'bg-neutral50 text-neutral400 border border-neutral400'
    },

    amber: {
      filled: 'bg-amber500 text-white hover:bg-amber400 hover:text-white active:bg-amber600 active:text-white',
      outline: 'border border-amber500 text-amber500 hover:border-amber400 hover:text-amber400 active:border-amber600 active:text-amber600',
      disabled: 'bg-neutral50 text-neutral400 border border-neutral400'
    }

  };


private sizeMap = {
  sm: 'h-[30px] px-[14px] !text-sm rounded-[6px] !leading-[16px]',
  md: 'h-[36px] px-[20px] !text-sm rounded-[6px] !leading-[16px]',
  lg: 'h-[46px] px-[24px] !text-md rounded-[6px] !leading-[22px]'
};

private borderedSizeMap = {
  sm: 'h-[30px] px-[14px] !text-sm rounded-[6px] !leading-[16px]',
  md: 'h-[36px] px-[20px] !text-sm rounded-[6px] !leading-[16px]',
  lg: 'h-[46px] px-[24px] !text-md rounded-[6px] !leading-[22px]'
};

private iconSizeMap = {
  sm: 12,
  md: 14,
  lg: 16
};

private iconContainerSizeMap = {
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5'
};

  get iconSize(): number {
    return this.iconSizeMap[this.size];
  }

  get iconContainerClasses(): string {
    return `inline-flex items-center justify-center shrink-0 ${this.iconContainerSizeMap[this.size]}`;
  }

  get projectedContentClasses(): string {
    return `inline-flex items-center leading-none ${this.iconTextGap}`;
  }

  get isBorderedButton(): boolean {
    if (this.disabled || this.loading) {
      return true;
    }

    return this.appearance === 'outline' || (this.variant === 'neutral' && this.appearance === 'filled');
  }

  get computedClasses(): string {
    const base =
      `inline-flex items-center justify-center ${this.iconTextGap} !font-semibold focus:outline-none`;

    const size = this.isBorderedButton ? this.borderedSizeMap[this.size] : this.sizeMap[this.size];
    const width = this.fullWidth ? 'w-full' : '';

    if (this.disabled || this.loading) {
      return `
        ${base}
        ${size}
        ${width}
        ${this.styles[this.variant].disabled}
        ${this.extraClasses}
      `;
    }

    return `
      ${base}
      ${size}
      ${width}
      cursor-pointer
      ${this.styles[this.variant][this.appearance]}
      ${this.extraClasses}
    `;
  }
  onClick(event: Event) {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }
}